import { readFileSync } from "node:fs";
import { parse, stringify } from "yaml";
import { globToRegex, isGlobPattern } from "./refPatterns.js";
import { SAFE_TOOLS } from "./toolAllowlist.js";

/**
 * A single test that an operator has declared "skip in the pre-merge
 * required_check suite" for this branch. Carried on `CheckDef.quarantine`
 * and on the resulting `CheckResult` / `CheckAttestation` so the
 * declaration is folded into the signed merge attestation.
 *
 * Stamp does NOT enforce the skip — it surfaces the declaration via the
 * `STAMP_QUARANTINE_TESTS` env var (comma-joined `test` IDs) to the
 * check's shell command, which is free to interpret the IDs in whatever
 * shape its test runner expects (vitest filename, Jest pattern, Go test
 * prefix, etc.). The trust property is "the declared list at merge time
 * is signed and auditable" — NOT "stamp guarantees the listed tests
 * were the only ones skipped." A verifier can audit which gates the
 * operator declared not-enforced for each merge.
 *
 * Adding or removing a quarantine entry is admin-gated automatically:
 * the change is to `.stamp/config.yml`, which is under `.stamp/**` and
 * already gated by the existing `path_rules` machinery.
 */
export interface QuarantineEntry {
  /** Test ID (or pattern). Free-form — interpretation is the check
   *  command's responsibility. Non-empty string. */
  test: string;
  /** Operator-supplied reason. Folded into the attestation so a future
   *  auditor sees WHY this gate was skipped. Non-empty string. */
  reason: string;
}

export interface CheckDef {
  /** Short name used in config and attestation payloads — e.g. "build", "test" */
  name: string;
  /** Shell command to run; non-zero exit blocks merge */
  run: string;
  /**
   * Optional opt-in flake quarantine: tests the operator has declared
   * skipped for this check. When non-empty, the check runner sets
   * `STAMP_QUARANTINE_TESTS` (comma-joined `test` IDs) in the spawn env,
   * and the full list is folded into the merge attestation so verifiers
   * can audit which gates were not enforced. See `QuarantineEntry`.
   *
   * Absent or empty array → no env var is set, no quarantine field
   * appears in the attestation, and the resulting envelope is
   * byte-identical to pre-AGT-476 envelopes. The opt-in shape is
   * deliberate so repos without quarantine see zero attestation churn.
   */
  quarantine?: QuarantineEntry[];
}

export interface BranchRule {
  required: string[];
  /** Optional pre-merge check commands; all must pass before merge is signed */
  required_checks?: CheckDef[];
  /**
   * When undefined or true, `stamp merge` requires explicit operator
   * confirmation (interactive y/N prompt, --yes flag, or
   * STAMP_REQUIRE_HUMAN_MERGE=0 env var) before signing. When false, merges
   * proceed unattended. Closes audit H1 (LLM-verdict-→-signed-merge
   * residual risk) by making operator awareness the default — the value
   * lives in committed config so changing it itself goes through stamp
   * review.
   *
   * When set to `"strict"`, the confirmation prompt is sharpened: instead
   * of a bare y/N, the operator must type the exact phrase
   * `merge <source> -> <target>` to confirm. Useful for branches where an
   * accidental confirmation is especially costly. Does NOT override the
   * --yes / STAMP_REQUIRE_HUMAN_MERGE=0 / require_human_merge: false
   * opt-outs — those still bypass the prompt entirely.
   */
  require_human_merge?: boolean | "strict";
  /**
   * PR-check mode only. When true, `stamp verify-pr` requires that the
   * tip of the target branch is the SAME as it was when the reviewer
   * signed — i.e. `attestation.target_branch_tip_sha` must equal
   * `git rev-parse <target>` at verify time. Any advancement of the
   * target branch since attest invalidates, INCLUDING unrelated
   * commits that don't touch the merge-base. (This is why the check
   * is on the tip and not on `base_sha` / merge-base — those don't
   * change when main moves with unrelated commits, but the tip does.)
   *
   * Default (undefined) is loose: attestation remains valid as long as
   * the patch-id matches, regardless of where main has moved since the
   * reviewer signed. This matches GitHub's "approval persists across
   * base advancement" semantic — operators using PR-check mode expect
   * the same shape.
   *
   * Server-gated mode (`stamp merge` → trailer-on-merge-commit) ignores
   * this field; its attestations are pinned to (base_sha, head_sha)
   * by construction and always strict.
   */
  strict_base?: boolean;
  /**
   * Stamp 2.x server-attested reviews (AGT-332). When set, `stamp review`
   * routes each reviewer through this URL's `stamp-review` SSH verb
   * instead of calling the local Anthropic SDK. Shape is
   * `ssh://<user>@<host>:<port>` — same form as the bare-repo remote in
   * server-gated deployments. Absent → trusted mode falls back to the
   * 1.x local LLM path (warn-and-proceed; 1.x compatibility is the
   * documented contract through AGT-347).
   *
   * Sourced from the merge-base tree like every other field on this
   * struct, so a feature branch cannot point itself at an attacker-
   * controlled review server — the change goes through the regular
   * reviewer gate first.
   */
  review_server?: string;
}

/**
 * A single entry in a reviewer's `tools:` list. Either:
 *   - a bare string for a tool that has no per-tool config (Read, Grep, Glob)
 *   - an object form `{ name, allowed_hosts?, path_prefix? }` for tools that
 *     need per-call gating. WebFetch REQUIRES the object form with a non-empty
 *     `allowed_hosts` array — a bare `"WebFetch"` is rejected at invocation
 *     time because an unrestricted WebFetch is an exfiltration channel for
 *     diff content (a malicious diff plants a URL, the reviewer follows it,
 *     the diff context flows out).
 *
 *     `allowed_hosts` is a *domain-level* allowlist. To pin the URL shape
 *     too — e.g. only `/repos/` paths on `api.github.com` — set
 *     `path_prefix` on the same entry. When present, the runtime hook
 *     rejects any URL whose `URL.pathname` does not begin with that prefix.
 *     AGT-036 / audit M4.
 *
 *     The query string is NOT inspected by default (GitHub/Linear/Notion
 *     APIs use it legitimately), which makes an allowlisted host a
 *     diff-exfil channel under prompt injection. Constrain it with
 *     `query_param_allowlist` (permitted param names) and/or
 *     `query_param_max_length` (cap on total query-value length). AGT-419.
 */
export type ToolSpec =
  | string
  | {
      name: string;
      allowed_hosts?: string[];
      path_prefix?: string;
      /**
       * WebFetch only (AGT-419). Per-host allowlist of permitted query
       * parameter NAMES. When set, a URL carrying any query param outside
       * this set is rejected — closing the exfil channel where a
       * prompt-injected reviewer encodes context into query values on an
       * otherwise-allowlisted host (whose request log captures the query
       * regardless of whether the request succeeds).
       */
      query_param_allowlist?: string[];
      /**
       * WebFetch only (AGT-419). Cap on the total length (chars) summed
       * across all decoded query *values*. Long enough for legitimate
       * issue IDs, too short for meaningful exfil chunks. Independent of
       * `query_param_allowlist`; either or both may be set.
       */
      query_param_max_length?: number;
    };

/**
 * Loose, policy-free ToolSpec parser used wherever the SAFE_TOOLS policy
 * doesn't apply (hash verification path, network-fetched config). Accepts
 * both string shorthand and object form `{ name, allowed_hosts? }` and
 * filters out structurally-invalid entries silently — callers that need
 * strict validation use `parseTools` (config-load path) instead. Single
 * implementation shared by reviewerHash + reviewers-fetch so the two paths
 * cannot drift on schema additions.
 */
export function parseToolsLoose(input: unknown[]): ToolSpec[] {
  const out: ToolSpec[] = [];
  for (const entry of input) {
    if (typeof entry === "string") {
      if (entry) out.push(entry);
      continue;
    }
    if (entry && typeof entry === "object" && !Array.isArray(entry)) {
      const e = entry as Record<string, unknown>;
      if (typeof e.name !== "string" || !e.name) continue;
      const spec: {
        name: string;
        allowed_hosts?: string[];
        path_prefix?: string;
        query_param_allowlist?: string[];
        query_param_max_length?: number;
      } = { name: e.name };
      if (Array.isArray(e.allowed_hosts)) {
        const hosts = e.allowed_hosts.filter(
          (h): h is string => typeof h === "string" && h.length > 0,
        );
        if (hosts.length > 0) spec.allowed_hosts = hosts;
      }
      // Mirror the strict parser: only carry path_prefix when it's a
      // non-empty string starting with "/". Anything else is treated as
      // absent so the loose-parsed canonical form stays hash-equivalent
      // with what the strict parser would have accepted.
      if (
        typeof e.path_prefix === "string" &&
        e.path_prefix.length > 0 &&
        e.path_prefix.startsWith("/")
      ) {
        spec.path_prefix = e.path_prefix;
      }
      // AGT-419: mirror the strict parser's canonical form for the query
      // controls so loose- and strict-parsed specs hash identically.
      // Drop-when-empty on the allowlist; only carry a positive-integer cap.
      if (Array.isArray(e.query_param_allowlist)) {
        const params = e.query_param_allowlist.filter(
          (p): p is string => typeof p === "string" && p.length > 0,
        );
        if (params.length > 0) spec.query_param_allowlist = params;
      }
      if (
        typeof e.query_param_max_length === "number" &&
        Number.isInteger(e.query_param_max_length) &&
        e.query_param_max_length > 0
      ) {
        spec.query_param_max_length = e.query_param_max_length;
      }
      out.push(spec);
    }
  }
  return out;
}

export interface ReviewerDef {
  /**
   * Path to the reviewer's prompt file, repo-root-relative
   * (e.g. `.stamp/reviewers/security.md`). OPTIONAL since Shape 4
   * (server-attested without code transfer, AGT-370/372): when the
   * active branch rule declares a `review_server`, the stamp-server
   * resolves the prompt from its bundled cache by reviewer name and
   * the client never reads prompt bytes — so a Shape 4 `.stamp/config.yml`
   * names reviewers without a `prompt:` field at all.
   *
   * When this field is omitted and a local-only path needs the bytes
   * (`stamp review --plan`, `stamp review --headless`, or `stamp review`
   * against a branch rule with no `review_server:`), the command errors
   * out naming the reviewer and the missing field. See `reviewPlan.ts`
   * and `runReview` for the call sites.
   */
  prompt?: string;
  /**
   * Claude Agent SDK built-in tools the reviewer may call during review.
   * The set of permitted tool names is constrained at invocation time to
   * the SAFE_TOOLS list in lib/toolAllowlist.ts (read-only investigation
   * tools only — Edit / Write / Task remain disallowed unconditionally).
   *
   * Bash is the one exception: it is NOT in `SAFE_TOOLS` (cannot be listed
   * here) but is reachable via the separate per-reviewer `bash: true`
   * opt-in field below — see that field for the security model.
   *
   * Object form (e.g. `{ name: "WebFetch", allowed_hosts: ["linear.app"] }`)
   * is required for tools that need per-call gating. Plain strings remain
   * supported for tools without per-tool config.
   *
   * Absent or empty → reviewer runs with zero tools (safe default).
   */
  tools?: ToolSpec[];
  /**
   * MCP servers to expose to the reviewer agent. Keys are server names used
   * in the reviewer prompt (e.g. "linear"); values are stdio server configs.
   * Env values may reference shell env vars via $VAR or ${VAR} — resolved at
   * invocation time, gated by an allowlist (operator env
   * `STAMP_REVIEWER_ENV_ALLOWLIST` and/or per-server `allowed_env`); unset
   * or non-allowlisted names cause `stamp review` to fail fast.
   */
  mcp_servers?: Record<string, McpServerDef>;
  /**
   * When true, this reviewer cannot return `approved` for a diff that
   * touches `.stamp/` paths unless its agent has called `Read` on every
   * modified `.stamp/*` file during the review. Verdict-↔-trace
   * consistency check: prevents a prompt-injected reviewer from waving
   * through a change to its own trust anchors (config.yml, reviewer
   * prompts, trusted-keys/) without inspecting the diff.
   *
   * Defaults to false (back-compat). Recommended on for whichever
   * reviewer is responsible for trust-anchor scrutiny — typically the
   * `security` persona, but the field is reviewer-name-agnostic so
   * operators with custom reviewer sets can opt their own in. Audit-H1
   * defense-in-depth alongside the default-on operator confirmation
   * gate.
   */
  enforce_reads_on_dotstamp?: boolean;
  /**
   * Per-reviewer cap on model/tool round-trips for this reviewer's
   * subprocess. Overrides the global `STAMP_REVIEWER_MAX_TURNS` env var
   * (default 8) when set. Use when one reviewer legitimately needs
   * headroom (e.g. heavy external lookup tools like a Linear MCP) but
   * raising the global budget would over-budget reviewers that don't.
   *
   * Must be a positive integer. Sourced from the merge-base tree along
   * with the rest of `.stamp/config.yml`, so a feature branch cannot
   * unilaterally widen its own review budget — changes go through the
   * reviewer gate like any other policy edit.
   */
  max_turns?: number;
  /**
   * Per-reviewer wall-clock budget in milliseconds. Overrides the global
   * `STAMP_REVIEWER_TIMEOUT_MS` env var (default 300000) when set. Same
   * shape, rationale, and merge-base-tree sourcing as `max_turns`.
   */
  timeout_ms?: number;
  /**
   * AGT-472: per-reviewer opt-in to the SDK `Bash` tool. When true,
   * `invokeReviewer` appends `"Bash"` to `allowedTools` and
   * `checkReviewerTool` lets Bash calls through; default (absent/false) is
   * deny, matching the `SAFE_TOOLS` policy in `toolAllowlist.ts`. This is
   * the escape hatch for reviewers that legitimately need shell access
   * (e.g. running `git log` to inspect commit history beyond the diff).
   *
   * Security model: `bash` is folded into the reviewer-prompt hash chain
   * via `hashTools` (a synthetic `"__bash"` sentinel is appended to the
   * canonicalized tools list when `bash === true`), so flipping a
   * reviewer from `bash: false → true` changes its `tools_sha256` and is
   * visible at `stamp verify` time. A feature branch cannot silently
   * widen its own reviewer's capabilities — the config edit goes through
   * the same merge gate as any other `.stamp/` change, and the
   * attestation chain visibly differs.
   *
   * Operators enabling this should understand that the SDK Bash tool
   * gives the reviewer unconstrained shell within the repoRoot cwd. We
   * deliberately do NOT gate the command string here — that would be a
   * Maginot Line; the threat model is "operator opted in, signed merges
   * record the choice."
   */
  bash?: boolean;
}

export interface McpServerDef {
  command: string;
  args?: string[];
  env?: Record<string, string>;
  /**
   * Per-server opt-in allowlist of operator env-var names that this server's
   * `env:` `$VAR` / `${VAR}` references may resolve. Optional; the operator
   * env `STAMP_REVIEWER_ENV_ALLOWLIST` is the other allowlist source. A
   * `$VAR` reference resolves iff `VAR` appears in the union of these two
   * sources AND is set in `process.env`. Names must be POSIX identifiers
   * (`[A-Za-z_][A-Za-z0-9_]*`) and are validated at config-load time.
   */
  allowed_env?: string[];
  /**
   * When true, a launch/connection failure for this MCP server is treated as
   * a non-fatal degradation: stamp emits a stderr warning and proceeds with
   * the reduced tool surface rather than aborting the review. Absent or false
   * (the default) means any non-`connected` status after init hard-fails the
   * reviewer with exit 1.
   *
   * IMPORTANT (hash stability): only emit this key in the parsed
   * `McpServerDef` when the YAML explicitly sets it — `optional: true` or
   * `optional: false` — so configs that omit the field hash byte-identically
   * to their pre-AGT-246 forms (canonical JSON only includes present keys).
   * Mirror the `...(allowed_env ? { allowed_env } : {})` omit-on-unset
   * pattern used by every other optional field in parseMcpServers.
   */
  optional?: boolean;
}

/**
 * POSIX env-var identifier shape. Used to validate `allowed_env` entries at
 * config-load time (strict — config bytes are committed and re-hashed by
 * verifiers, a typo there is a bug worth surfacing) and to filter the
 * comma-separated `STAMP_REVIEWER_ENV_ALLOWLIST` values at invocation time
 * (lenient — silently drop malformed names rather than block a review on
 * a harness-injected garbage env string).
 */
export const ENV_IDENTIFIER_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Optional top-level `data_flow:` block (AGT-415). Operator-authored
 * disclosure that `stamp review` echoes, plus an opt-in confirmation gate
 * for regulated repos. Every `stamp review` ships the diff off-host to a
 * sub-processor (Anthropic, directly or via a `review_server`); this block
 * lets an operator commit a project-specific disclosure and — for repos
 * under regulated content — require an explicit committed acknowledgement
 * before any review runs.
 *
 * IMPORTANT (security): this is a TOP-LEVEL field. It does NOT enter the
 * v3/v4 reviewer attestation hash chain (which hashes only per-reviewer
 * `prompt`/`tools`/`mcp_servers` — see `reviewerHash.ts`), so it is a
 * purely additive config field: existing attestations and `stamp verify`
 * are unaffected. Like every other policy field, `stamp review` reads it
 * from the merge-base tree, NOT the working tree — so a feature branch
 * cannot ship its own `confirmed: true` to wave its own introduction past
 * the gate.
 */
export interface DataFlowConfig {
  /**
   * Operator-authored sub-processor disclosure prose. When present,
   * `stamp review` echoes it to stderr on every run (AC #2). Free-form;
   * multi-line is fine. Echo is silenced by `STAMP_SUPPRESS_LLM_NOTICE=1`
   * along with the rest of the data-flow notices.
   */
  disclosure?: string;
  /**
   * Opt-in regulated marker (AC #3). When `true`, `stamp review` REFUSES
   * to run unless `confirmed: true` is also committed. Absent/false means
   * the block is disclosure-only: it echoes (if `disclosure` is set) but
   * never blocks. This is the ONLY field that arms the gate — adding
   * disclosure text alone never bricks review for a non-regulated repo.
   */
  require_confirmation?: boolean;
  /**
   * Operator's committed acknowledgement of the data flow. Only meaningful
   * when `require_confirmation: true`; in that case review proceeds iff
   * this is exactly `true`. Committing it (rather than passing a flag at
   * runtime) is deliberate: the acknowledgement itself goes through stamp
   * review, leaving an auditable record of who accepted the sub-processor
   * disclosure.
   */
  confirmed?: boolean;
}

/**
 * Optional top-level `retention:` block (AGT-112). Drives post-review
 * advisory output and opt-in auto-prune.
 *
 * IMPORTANT (security): this is a TOP-LEVEL field. It does NOT enter the
 * v3/v4 reviewer attestation hash chain (which hashes only per-reviewer
 * `prompt`/`tools`/`mcp_servers` — see `reviewerHash.ts`), so it is a
 * purely additive config field: existing attestations and `stamp verify`
 * are unaffected. Like every other policy field, `stamp review` reads it
 * from the merge-base tree, NOT the working tree.
 */
export interface RetentionConfig {
  /**
   * Duration string (`<n>d`, `<n>h`, `<n>m`) for review rows in
   * `state.db`. When set and rows older than this threshold exist after a
   * `stamp review` run, an advisory is printed to stderr suggesting the
   * operator run `stamp prune --older-than <duration>`.
   */
  reviews?: string;
  /**
   * Duration string (`<n>d`, `<n>h`, `<n>m`) for failed-parse and
   * failed-run spool files. Same advisory mechanic as `reviews`.
   */
  spools?: string;
  /**
   * When true, `stamp review` auto-prunes at the configured thresholds
   * (via `stamp prune`) instead of just advising. Default (unset/false)
   * stays advisory-only so an operator's prose history is never silently
   * deleted — matches stamp's "explicit opt-in" pattern.
   */
  auto_prune?: boolean;
}

export interface StampConfig {
  branches: Record<string, BranchRule>;
  reviewers: Record<string, ReviewerDef>;
  /** Optional sub-processor disclosure + confirmation gate (AGT-415). */
  data_flow?: DataFlowConfig;
  /** Optional retention advisory / auto-prune config (AGT-112). */
  retention?: RetentionConfig;
}

export function loadConfig(path: string): StampConfig {
  return parseConfigFromYaml(readFileSync(path, "utf8"));
}

/**
 * Parse a .stamp/config.yml text blob into a validated StampConfig.
 * Delegated from loadConfig and also used by verify-at-sha paths that
 * source the YAML via `git show <sha>:.stamp/config.yml`. Single parser
 * keeps the verifier and the working-tree loader in sync — if one grows
 * a new field, both paths see it.
 */
export function parseConfigFromYaml(raw: string): StampConfig {
  const parsed = parse(raw) as unknown;
  return validateConfig(parsed);
}

function validateConfig(input: unknown): StampConfig {
  if (!input || typeof input !== "object") {
    throw new Error("config must be an object");
  }
  const obj = input as Record<string, unknown>;

  const branches: Record<string, BranchRule> = {};
  const rawBranches = obj.branches;
  if (!rawBranches || typeof rawBranches !== "object") {
    throw new Error("config.branches must be an object");
  }
  for (const [name, rule] of Object.entries(rawBranches)) {
    if (!rule || typeof rule !== "object") {
      throw new Error(`config.branches.${name} must be an object`);
    }
    const r = rule as Record<string, unknown>;
    if (!Array.isArray(r.required)) {
      throw new Error(`config.branches.${name}.required must be an array`);
    }

    const required_checks = parseChecks(r.required_checks, name);

    let require_human_merge: boolean | "strict" | undefined;
    if (r.require_human_merge !== undefined) {
      if (r.require_human_merge === "strict") {
        require_human_merge = "strict";
      } else if (typeof r.require_human_merge === "boolean") {
        require_human_merge = r.require_human_merge;
      } else {
        throw new Error(
          `config.branches.${name}.require_human_merge must be a boolean or "strict" ` +
            `(got ${JSON.stringify(r.require_human_merge)})`,
        );
      }
    }

    let strict_base: boolean | undefined;
    if (r.strict_base !== undefined) {
      if (typeof r.strict_base !== "boolean") {
        throw new Error(
          `config.branches.${name}.strict_base must be a boolean`,
        );
      }
      strict_base = r.strict_base;
    }

    let review_server: string | undefined;
    if (r.review_server !== undefined) {
      if (typeof r.review_server !== "string" || !r.review_server.trim()) {
        throw new Error(
          `config.branches.${name}.review_server must be a non-empty string ` +
            `(e.g. "ssh://git@stamp.example.com:22")`,
        );
      }
      // Shape-validate at parse time so a typo or wrong-scheme URL
      // surfaces as a config error rather than as an SSH connection
      // failure several seconds into `stamp review`. The full URL parser
      // in `src/lib/sshReviewClient.ts` re-validates on use; this is
      // the cheap pre-flight.
      const trimmed = r.review_server.trim();
      if (!trimmed.startsWith("ssh://")) {
        throw new Error(
          `config.branches.${name}.review_server must be an ssh:// URL ` +
            `(got ${JSON.stringify(trimmed)})`,
        );
      }
      review_server = trimmed;
    }

    branches[name] = {
      required: r.required.map(String),
      ...(required_checks ? { required_checks } : {}),
      ...(require_human_merge !== undefined ? { require_human_merge } : {}),
      ...(strict_base !== undefined ? { strict_base } : {}),
      ...(review_server !== undefined ? { review_server } : {}),
    };
  }

  const reviewers: Record<string, ReviewerDef> = {};
  const rawReviewers = obj.reviewers;
  if (!rawReviewers || typeof rawReviewers !== "object") {
    throw new Error("config.reviewers must be an object");
  }
  for (const [name, def] of Object.entries(rawReviewers)) {
    if (!def || typeof def !== "object") {
      throw new Error(`config.reviewers.${name} must be an object`);
    }
    const d = def as Record<string, unknown>;
    // `prompt` became optional in Shape 4 (server-attested without code
    // transfer): when the active branch rule has a `review_server:` the
    // server-side prompt cache is the canonical source and the client
    // never needs the bytes. If present, it must still be a string;
    // omitted is fine and downstream local-only paths surface a clear
    // error if they actually need the bytes.
    let prompt: string | undefined;
    if (d.prompt !== undefined) {
      if (typeof d.prompt !== "string") {
        throw new Error(`config.reviewers.${name}.prompt must be a string`);
      }
      prompt = d.prompt;
    }
    const tools = parseTools(d.tools, name);
    const mcp_servers = parseMcpServers(d.mcp_servers, name);

    let enforce_reads_on_dotstamp: boolean | undefined;
    if (d.enforce_reads_on_dotstamp !== undefined) {
      if (typeof d.enforce_reads_on_dotstamp !== "boolean") {
        throw new Error(
          `config.reviewers.${name}.enforce_reads_on_dotstamp must be a boolean (got ${JSON.stringify(d.enforce_reads_on_dotstamp)})`,
        );
      }
      enforce_reads_on_dotstamp = d.enforce_reads_on_dotstamp;
    }

    const max_turns = parsePositiveInt(
      d.max_turns,
      `config.reviewers.${name}.max_turns`,
    );
    const timeout_ms = parsePositiveInt(
      d.timeout_ms,
      `config.reviewers.${name}.timeout_ms`,
    );

    // AGT-472: per-reviewer Bash opt-in. Omit-on-unset to preserve
    // byte-identical hash chains for pre-AGT-472 configs.
    let bash: boolean | undefined;
    if (d.bash !== undefined) {
      if (typeof d.bash !== "boolean") {
        throw new Error(
          `config.reviewers.${name}.bash must be a boolean (got ${JSON.stringify(d.bash)})`,
        );
      }
      bash = d.bash;
    }

    reviewers[name] = {
      ...(prompt !== undefined ? { prompt } : {}),
      ...(tools ? { tools } : {}),
      ...(mcp_servers ? { mcp_servers } : {}),
      ...(enforce_reads_on_dotstamp !== undefined
        ? { enforce_reads_on_dotstamp }
        : {}),
      ...(max_turns !== undefined ? { max_turns } : {}),
      ...(timeout_ms !== undefined ? { timeout_ms } : {}),
      ...(bash !== undefined ? { bash } : {}),
    };
  }

  const data_flow = parseDataFlow(obj.data_flow);
  const retention = parseRetention(obj.retention);

  return {
    branches,
    reviewers,
    ...(data_flow ? { data_flow } : {}),
    ...(retention ? { retention } : {}),
  };
}

function parseDataFlow(input: unknown): DataFlowConfig | undefined {
  if (input === undefined || input === null) return undefined;
  if (typeof input !== "object" || Array.isArray(input)) {
    throw new Error("config.data_flow must be an object");
  }
  const d = input as Record<string, unknown>;

  let disclosure: string | undefined;
  if (d.disclosure !== undefined) {
    if (typeof d.disclosure !== "string") {
      throw new Error("config.data_flow.disclosure must be a string");
    }
    disclosure = d.disclosure;
  }

  let require_confirmation: boolean | undefined;
  if (d.require_confirmation !== undefined) {
    if (typeof d.require_confirmation !== "boolean") {
      throw new Error(
        `config.data_flow.require_confirmation must be a boolean (got ${JSON.stringify(d.require_confirmation)})`,
      );
    }
    require_confirmation = d.require_confirmation;
  }

  let confirmed: boolean | undefined;
  if (d.confirmed !== undefined) {
    if (typeof d.confirmed !== "boolean") {
      throw new Error(
        `config.data_flow.confirmed must be a boolean (got ${JSON.stringify(d.confirmed)})`,
      );
    }
    confirmed = d.confirmed;
  }

  return {
    ...(disclosure !== undefined ? { disclosure } : {}),
    ...(require_confirmation !== undefined ? { require_confirmation } : {}),
    ...(confirmed !== undefined ? { confirmed } : {}),
  };
}

function parseRetention(input: unknown): RetentionConfig | undefined {
  if (input === undefined || input === null) return undefined;
  if (typeof input !== "object" || Array.isArray(input)) {
    throw new Error("config.retention must be an object");
  }
  const d = input as Record<string, unknown>;

  let reviews: string | undefined;
  if (d.reviews !== undefined) {
    if (typeof d.reviews !== "string") {
      throw new Error("config.retention.reviews must be a string (e.g. \"90d\")");
    }
    // Validate the duration shape up-front so a typo surfaces at config-load
    // time rather than at the advisory call site — same pattern as the CLI's
    // `stamp prune --older-than` validation.
    try {
      parseRetentionDurationInternal(d.reviews);
    } catch (err) {
      throw new Error(
        `config.retention.reviews: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
    reviews = d.reviews;
  }

  let spools: string | undefined;
  if (d.spools !== undefined) {
    if (typeof d.spools !== "string") {
      throw new Error("config.retention.spools must be a string (e.g. \"30d\")");
    }
    try {
      parseRetentionDurationInternal(d.spools);
    } catch (err) {
      throw new Error(
        `config.retention.spools: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
    spools = d.spools;
  }

  let auto_prune: boolean | undefined;
  if (d.auto_prune !== undefined) {
    if (typeof d.auto_prune !== "boolean") {
      throw new Error(
        `config.retention.auto_prune must be a boolean (got ${JSON.stringify(d.auto_prune)})`,
      );
    }
    auto_prune = d.auto_prune;
  }

  return {
    ...(reviews !== undefined ? { reviews } : {}),
    ...(spools !== undefined ? { spools } : {}),
    ...(auto_prune !== undefined ? { auto_prune } : {}),
  };
}

/**
 * Internal minimal duration validator. Mirrors parseRetentionDuration's regex
 * check without importing it — avoids a circular dependency between config.ts
 * and duration.ts. The real parser in lib/duration.ts is used at advisory
 * runtime; this is config-load-time preflight only.
 */
function parseRetentionDurationInternal(input: string): void {
  if (!/^[1-9][0-9]{0,6}(d|h|m)$/.test(input)) {
    throw new Error(
      `invalid duration "${input}". Accepted shapes: <n>d (days), <n>h (hours), <n>m (minutes), where <n> is a positive integer (no whitespace, no leading +, no zero). Examples: 30d, 12h, 90m.`,
    );
  }
}

function parsePositiveInt(input: unknown, path: string): number | undefined {
  if (input === undefined || input === null) return undefined;
  if (typeof input !== "number" || !Number.isFinite(input) || !Number.isInteger(input) || input <= 0) {
    throw new Error(
      `${path} must be a positive integer (got ${JSON.stringify(input)})`,
    );
  }
  return input;
}

function parseChecks(input: unknown, branchName: string): CheckDef[] | undefined {
  if (input === undefined || input === null) return undefined;
  if (!Array.isArray(input)) {
    throw new Error(
      `config.branches.${branchName}.required_checks must be an array`,
    );
  }
  const out: CheckDef[] = [];
  for (const entry of input) {
    if (!entry || typeof entry !== "object") {
      throw new Error(
        `config.branches.${branchName}.required_checks entries must be objects`,
      );
    }
    const e = entry as Record<string, unknown>;
    if (typeof e.name !== "string" || !e.name) {
      throw new Error(
        `config.branches.${branchName}.required_checks[].name must be a non-empty string`,
      );
    }
    if (typeof e.run !== "string" || !e.run) {
      throw new Error(
        `config.branches.${branchName}.required_checks[].run must be a non-empty string`,
      );
    }
    const quarantine = parseQuarantine(e.quarantine, branchName, e.name);
    out.push({
      name: e.name,
      run: e.run,
      ...(quarantine ? { quarantine } : {}),
    });
  }
  return out;
}

function parseQuarantine(
  input: unknown,
  branchName: string,
  checkName: string,
): QuarantineEntry[] | undefined {
  if (input === undefined || input === null) return undefined;
  if (!Array.isArray(input)) {
    throw new Error(
      `config.branches.${branchName}.required_checks[name=${checkName}].quarantine must be an array`,
    );
  }
  // Empty array → return undefined so we don't fold an empty
  // `quarantine: []` into the attestation (preserves byte-identity with
  // pre-AGT-476 envelopes for repos that scaffold the field but leave
  // it empty).
  if (input.length === 0) return undefined;
  const out: QuarantineEntry[] = [];
  for (let i = 0; i < input.length; i++) {
    const entry = input[i];
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      throw new Error(
        `config.branches.${branchName}.required_checks[name=${checkName}].quarantine[${i}] must be an object`,
      );
    }
    const q = entry as Record<string, unknown>;
    if (typeof q.test !== "string" || !q.test) {
      throw new Error(
        `config.branches.${branchName}.required_checks[name=${checkName}].quarantine[${i}].test must be a non-empty string`,
      );
    }
    if (typeof q.reason !== "string" || !q.reason) {
      throw new Error(
        `config.branches.${branchName}.required_checks[name=${checkName}].quarantine[${i}].reason must be a non-empty string ` +
          `— operator-supplied rationale is mandatory so future auditors see WHY this gate was skipped`,
      );
    }
    out.push({ test: q.test, reason: q.reason });
  }
  return out;
}

function parseTools(input: unknown, reviewerName: string): ToolSpec[] | undefined {
  if (input === undefined || input === null) return undefined;
  if (!Array.isArray(input)) {
    throw new Error(
      `config.reviewers.${reviewerName}.tools must be an array`,
    );
  }
  const safeSet = new Set<string>(SAFE_TOOLS);
  const out: ToolSpec[] = [];
  for (let i = 0; i < input.length; i++) {
    const entry = input[i];

    // String form: shorthand for tools without per-tool config.
    if (typeof entry === "string") {
      if (!entry) {
        throw new Error(
          `config.reviewers.${reviewerName}.tools[${i}] is an empty string`,
        );
      }
      if (!safeSet.has(entry)) {
        throw new Error(
          `config.reviewers.${reviewerName}.tools[${i}] = "${entry}" is not in the SAFE_TOOLS set ` +
            `(${SAFE_TOOLS.join(", ")}). Adding a new tool requires a code change to ` +
            `src/lib/toolAllowlist.ts so the addition is reviewed and signed.`,
        );
      }
      if (entry === "WebFetch") {
        throw new Error(
          `config.reviewers.${reviewerName}.tools[${i}] = "WebFetch" must use the object form ` +
            `with a non-empty allowed_hosts list, e.g. { name: "WebFetch", allowed_hosts: ["linear.app"] }. ` +
            `An unrestricted WebFetch lets a malicious diff plant a URL the reviewer will follow, ` +
            `exfiltrating diff context to attacker-chosen destinations.`,
        );
      }
      out.push(entry);
      continue;
    }

    // Object form: required for tools with per-call gating (currently WebFetch).
    if (entry && typeof entry === "object" && !Array.isArray(entry)) {
      const e = entry as Record<string, unknown>;
      if (typeof e.name !== "string" || !e.name) {
        throw new Error(
          `config.reviewers.${reviewerName}.tools[${i}].name must be a non-empty string`,
        );
      }
      if (!safeSet.has(e.name)) {
        throw new Error(
          `config.reviewers.${reviewerName}.tools[${i}].name = "${e.name}" is not in the SAFE_TOOLS set ` +
            `(${SAFE_TOOLS.join(", ")}). Adding a new tool requires a code change to ` +
            `src/lib/toolAllowlist.ts so the addition is reviewed and signed.`,
        );
      }
      // allowed_hosts is meaningful only for tools with per-call host gating
      // (currently just WebFetch). Reject it on other tools rather than
      // silently accepting — silently-accepted-but-ignored fields drift
      // into hash divergence between the strict and loose parsers and
      // confuse operators about which fields actually do something.
      if (e.allowed_hosts !== undefined && e.name !== "WebFetch") {
        throw new Error(
          `config.reviewers.${reviewerName}.tools[${i}].allowed_hosts is only valid on WebFetch ` +
            `(got name="${e.name}"). Remove the field or change the entry to use WebFetch.`,
        );
      }
      if (e.path_prefix !== undefined && e.name !== "WebFetch") {
        throw new Error(
          `config.reviewers.${reviewerName}.tools[${i}].path_prefix is only valid on WebFetch ` +
            `(got name="${e.name}"). Remove the field or change the entry to use WebFetch.`,
        );
      }
      if (e.query_param_allowlist !== undefined && e.name !== "WebFetch") {
        throw new Error(
          `config.reviewers.${reviewerName}.tools[${i}].query_param_allowlist is only valid on WebFetch ` +
            `(got name="${e.name}"). Remove the field or change the entry to use WebFetch.`,
        );
      }
      if (e.query_param_max_length !== undefined && e.name !== "WebFetch") {
        throw new Error(
          `config.reviewers.${reviewerName}.tools[${i}].query_param_max_length is only valid on WebFetch ` +
            `(got name="${e.name}"). Remove the field or change the entry to use WebFetch.`,
        );
      }
      const spec: {
        name: string;
        allowed_hosts?: string[];
        path_prefix?: string;
        query_param_allowlist?: string[];
        query_param_max_length?: number;
      } = { name: e.name };
      if (e.allowed_hosts !== undefined) {
        if (!Array.isArray(e.allowed_hosts)) {
          throw new Error(
            `config.reviewers.${reviewerName}.tools[${i}].allowed_hosts must be an array of strings`,
          );
        }
        const hosts: string[] = [];
        for (const h of e.allowed_hosts) {
          if (typeof h !== "string" || !h) {
            throw new Error(
              `config.reviewers.${reviewerName}.tools[${i}].allowed_hosts entries must be non-empty strings`,
            );
          }
          hosts.push(h);
        }
        // Match parseToolsLoose's canonical form: drop the property entirely
        // when the array is empty so both parsers produce hash-equivalent
        // output. The next check then fires the "WebFetch requires non-empty"
        // rule consistently for both string and object input shapes.
        if (hosts.length > 0) spec.allowed_hosts = hosts;
      }
      // path_prefix is opt-in. When present it must be a non-empty string
      // starting with "/" so the runtime check (`URL.pathname.startsWith(p)`)
      // is meaningful — a relative or empty value would either match
      // nothing or match everything, neither of which the operator would
      // expect from the YAML. AGT-036 / audit M4.
      if (e.path_prefix !== undefined) {
        if (typeof e.path_prefix !== "string") {
          throw new Error(
            `config.reviewers.${reviewerName}.tools[${i}].path_prefix must be a string`,
          );
        }
        if (e.path_prefix.length === 0) {
          throw new Error(
            `config.reviewers.${reviewerName}.tools[${i}].path_prefix must be non-empty`,
          );
        }
        if (!e.path_prefix.startsWith("/")) {
          throw new Error(
            `config.reviewers.${reviewerName}.tools[${i}].path_prefix must start with "/" ` +
              `(got "${e.path_prefix}"). Use the full URL path prefix, e.g. "/repos/" or "/api/".`,
          );
        }
        spec.path_prefix = e.path_prefix;
      }
      // query_param_allowlist (AGT-419): array of non-empty param-name
      // strings. Drop-when-empty to match parseToolsLoose's canonical form
      // (an empty allowlist would be indistinguishable from "no allowlist"
      // and must hash the same).
      if (e.query_param_allowlist !== undefined) {
        if (!Array.isArray(e.query_param_allowlist)) {
          throw new Error(
            `config.reviewers.${reviewerName}.tools[${i}].query_param_allowlist must be an array of strings`,
          );
        }
        const params: string[] = [];
        for (const p of e.query_param_allowlist) {
          if (typeof p !== "string" || !p) {
            throw new Error(
              `config.reviewers.${reviewerName}.tools[${i}].query_param_allowlist entries must be non-empty strings`,
            );
          }
          params.push(p);
        }
        if (params.length > 0) spec.query_param_allowlist = params;
      }
      // query_param_max_length (AGT-419): positive integer cap on total
      // decoded query-value length. parsePositiveInt throws on a non-
      // positive-integer and returns undefined when absent.
      const qpml = parsePositiveInt(
        e.query_param_max_length,
        `config.reviewers.${reviewerName}.tools[${i}].query_param_max_length`,
      );
      if (qpml !== undefined) spec.query_param_max_length = qpml;
      // WebFetch requires non-empty allowed_hosts (the bare-string and
      // empty-array paths both fail here). The runtime PreToolUse hook
      // assumes allowed_hosts is present and non-empty for any WebFetch
      // entry that reaches it.
      if (e.name === "WebFetch" && !spec.allowed_hosts) {
        throw new Error(
          `config.reviewers.${reviewerName}.tools[${i}] WebFetch requires a non-empty allowed_hosts list. ` +
            `In YAML block form:\n` +
            `    - name: WebFetch\n` +
            `      allowed_hosts: [linear.app, github.com]\n` +
            `Everything not in this list is denied at the SDK boundary via canUseTool.`,
        );
      }
      out.push(spec);
      continue;
    }
    throw new Error(
      `config.reviewers.${reviewerName}.tools[${i}] must be a tool name string or { name, allowed_hosts? } object`,
    );
  }
  return out;
}

function parseMcpServers(
  input: unknown,
  reviewerName: string,
): Record<string, McpServerDef> | undefined {
  if (input === undefined || input === null) return undefined;
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new Error(
      `config.reviewers.${reviewerName}.mcp_servers must be a map of server name → config`,
    );
  }
  const out: Record<string, McpServerDef> = {};
  for (const [serverName, raw] of Object.entries(input)) {
    if (!raw || typeof raw !== "object") {
      throw new Error(
        `config.reviewers.${reviewerName}.mcp_servers.${serverName} must be an object`,
      );
    }
    const r = raw as Record<string, unknown>;
    if (typeof r.command !== "string" || !r.command) {
      throw new Error(
        `config.reviewers.${reviewerName}.mcp_servers.${serverName}.command must be a non-empty string`,
      );
    }
    const args = r.args === undefined ? undefined : parseStringArray(
      r.args,
      `config.reviewers.${reviewerName}.mcp_servers.${serverName}.args`,
    );
    const env = r.env === undefined ? undefined : parseStringMap(
      r.env,
      `config.reviewers.${reviewerName}.mcp_servers.${serverName}.env`,
    );
    const allowed_env = r.allowed_env === undefined ? undefined : parseEnvIdentifierArray(
      r.allowed_env,
      `config.reviewers.${reviewerName}.mcp_servers.${serverName}.allowed_env`,
    );
    // Omit-on-unset: only include `optional` in the parsed object when the
    // YAML explicitly sets it. This keeps the canonical JSON (and thus
    // mcp_sha256) byte-identical to pre-AGT-246 for configs that don't
    // set `optional`. Mirrors the allowed_env + args omit-on-unset pattern.
    let optional: boolean | undefined;
    if (r.optional !== undefined) {
      if (typeof r.optional !== "boolean") {
        throw new Error(
          `config.reviewers.${reviewerName}.mcp_servers.${serverName}.optional must be a boolean (got ${JSON.stringify(r.optional)})`,
        );
      }
      optional = r.optional;
    }
    out[serverName] = {
      command: r.command,
      ...(args ? { args } : {}),
      ...(env ? { env } : {}),
      ...(allowed_env ? { allowed_env } : {}),
      ...(optional !== undefined ? { optional } : {}),
    };
  }
  return out;
}

/**
 * Parse `allowed_env` entries: array of POSIX env-var identifier strings.
 * Strict at config-load time — the bytes are committed and the hash flows
 * into `mcp_sha256` attestation, so a typo or invalid identifier here is a
 * config bug that should surface before the first review, not silently get
 * dropped (which is what `parseEnvAllowlist` does for the operator env var).
 *
 * Exported so the persona-fetch path in `commands/reviewers.ts` (which
 * builds its YAML-path prefixes from `${source}@${ref}`) reuses the same
 * regex + wording — single source of truth for the validator.
 */
export function parseEnvIdentifierArray(input: unknown, path: string): string[] {
  if (!Array.isArray(input)) {
    throw new Error(`${path} must be an array of POSIX env-var identifier strings`);
  }
  return input.map((v, i) => {
    if (typeof v !== "string") {
      throw new Error(`${path}[${i}] must be a string`);
    }
    if (!ENV_IDENTIFIER_REGEX.test(v)) {
      throw new Error(
        `${path}[${i}] "${v}" is not a valid POSIX env-var identifier ` +
          `(must match [A-Za-z_][A-Za-z0-9_]*)`,
      );
    }
    return v;
  });
}

function parseStringArray(input: unknown, path: string): string[] {
  if (!Array.isArray(input)) {
    throw new Error(`${path} must be an array of strings`);
  }
  return input.map((v, i) => {
    if (typeof v !== "string") {
      throw new Error(`${path}[${i}] must be a string`);
    }
    return v;
  });
}

function parseStringMap(input: unknown, path: string): Record<string, string> {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new Error(`${path} must be a map of string → string`);
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(input)) {
    if (typeof v !== "string") {
      throw new Error(`${path}.${k} must be a string`);
    }
    out[k] = v;
  }
  return out;
}

export function stringifyConfig(config: StampConfig): string {
  return stringify(config);
}

/**
 * Resolve the branch rule for a literal branch name. Map keys may be
 * literal branch names OR glob patterns (`*`, `?` — same grammar as
 * mirror.yml's `tags:` field; see refPatterns.ts).
 *
 * Resolution rule, exact-first then glob:
 *   1. If a key matches `branchName` literally, that rule wins. Exact
 *      keys without metacharacters never participate in glob matching.
 *   2. Otherwise, scan keys that contain `*` or `?` and test each as a
 *      glob. If exactly one matches, return it. If multiple match, throw
 *      with the conflicting keys named so the operator can disambiguate.
 *   3. If nothing matches, return undefined.
 *
 * The undefined return mirrors the prior `branches[name]` behavior so
 * callers that treat "no rule = unprotected" still work. Callers that
 * require a rule should keep their existing throw with the same wording.
 */
export function findBranchRule(
  branches: Record<string, BranchRule>,
  branchName: string,
): BranchRule | undefined {
  const exact = branches[branchName];
  if (exact !== undefined) return exact;

  const matchingKeys: string[] = [];
  for (const key of Object.keys(branches)) {
    if (!isGlobPattern(key)) continue;
    if (globToRegex(key).test(branchName)) matchingKeys.push(key);
  }
  if (matchingKeys.length === 0) return undefined;
  if (matchingKeys.length > 1) {
    throw new Error(
      `branch "${branchName}" matches multiple glob patterns in .stamp/config.yml: ${matchingKeys.map((k) => `"${k}"`).join(", ")}. ` +
        `Tighten the patterns or add an exact-match key for "${branchName}".`,
    );
  }
  return branches[matchingKeys[0]!];
}

/**
 * Default config scaffolded by `stamp init` (three-persona mode).
 * Main requires all three shipped reviewers. No required_checks by default —
 * users add their own per project (e.g. `npm run build`).
 */
export const DEFAULT_CONFIG: StampConfig = {
  branches: {
    main: {
      required: ["security", "standards", "product"],
    },
  },
  reviewers: {
    security: {
      prompt: ".stamp/reviewers/security.md",
      enforce_reads_on_dotstamp: true,
    },
    standards: { prompt: ".stamp/reviewers/standards.md" },
    product: { prompt: ".stamp/reviewers/product.md" },
  },
};

/**
 * Fallback config scaffolded by `stamp init --minimal`. One placeholder
 * reviewer, for users who want to start from scratch rather than customize
 * shipped defaults.
 */
export const MINIMAL_CONFIG: StampConfig = {
  branches: {
    main: { required: ["example"] },
  },
  reviewers: {
    example: { prompt: ".stamp/reviewers/example.md" },
  },
};

export const EXAMPLE_REVIEWER_PROMPT = `# example reviewer (bootstrap only — auto-approves everything)

> **WARNING — DO NOT use this reviewer for real code review.** It is a
> deterministic auto-approver intended only to land your *real* reviewers
> via the \`stamp bootstrap\` flow (or the manual placeholder→real swap
> documented in \`docs/troubleshooting.md\`). Once your real reviewers are
> in place, remove this one (or leave it defined-but-unrequired forever).
>
> If you reached this prompt via \`stamp init --minimal\`, **replace the
> entire body below** with your actual reviewer instructions before
> running any meaningful review.

## Instructions to the reviewer agent

You are a bootstrap-only placeholder reviewer. **Do not analyze the diff.**
Do not read files. Do not comment on the code. Output exactly the following
two-line response, verbatim, and nothing else:

\`\`\`
Bootstrap placeholder reviewer — approving unconditionally so real reviewers can be installed. Replace this reviewer before relying on it for actual code review.
VERDICT: approved
\`\`\`

That is the entire response. No preamble, no analysis, no caveats beyond
the line above. The \`VERDICT: approved\` line MUST be the final line.

## Why this exists

Every stamp-protected repo needs at least one reviewer that can approve
the very first merge — the merge that installs the *real* reviewers.
That's a chicken-and-egg problem: real reviewers can't approve their own
introduction. This placeholder solves it by being trivially approvable,
and is meant to be retired (or kept defined-but-unrequired) immediately
after.

For guidance on writing real reviewer prompts — structure, calibration,
verdict thresholds — see
https://github.com/OpenThinkAi/stamp-cli/blob/main/docs/personas.md.
\`stamp init\` (without \`--minimal\`) scaffolds three calibrated starter
personas (security / standards / product) you can customize.
`;

export const DEFAULT_SECURITY_PROMPT = `# security reviewer

You are the security reviewer for this project. Your job is to flag changes
that introduce exploitable issues, expose secrets, or widen the trust
boundary in ways the author may not have considered.

This prompt is a starting point. Edit it to reflect your project's actual
threat model and stack. See https://github.com/OpenThinkAi/stamp-cli/blob/main/docs/personas.md
for guidance on calibrating reviewer prompts.

## What to check for

1. **Committed secrets.** API keys, tokens, credentials, or environment-style
   values hardcoded in any tracked file. Even in tests, docs, or comments.
2. **Dependency risk.** New entries in the manifest (package.json,
   requirements, Cargo.toml, etc.) — obscure authors, names resembling
   popular packages (typosquats), install-time scripts, or unexplained
   major-version jumps.
3. **Dangerous primitives.** Any introduction of \`eval\`, \`Function\`
   constructors, \`innerHTML\` / \`{@html}\` with non-literal content, shell
   commands built from interpolation, or deserialization of untrusted input
   into privileged contexts.
4. **Input validation gaps at system boundaries.** User input, external API
   responses, filesystem paths from config — are these validated and
   bounded before use?
5. **Subprocess invocation.** \`exec\` / \`spawn\` with \`shell: true\` or with
   arguments composed from external data is an injection risk. Prefer
   argument-array forms.
6. **Outbound network calls.** New \`fetch\`, HTTP client, WebSocket, or
   similar. Is the destination expected for this project? Are secrets
   correctly scoped? Are response bodies trusted too readily?
7. **Secret leakage in logs or errors.** Does a new log line or error
   message include values that shouldn't surface (tokens, personal data,
   full file paths revealing infra)?
8. **Trust model changes.** Does the diff widen who can do what — add a
   bypass flag, relax a check, accept unsigned input somewhere it was
   previously signed?

## What you do NOT check

- Code style, idiom, abstraction choices → **standards** reviewer.
- User-facing interface decisions (UX, API shape, breaking changes) → **product** reviewer.

\`.stamp/\` changes ARE in scope: Read each modified \`.stamp/*\` file
before your verdict — you are reviewing stamp's own trust anchors
(reviewer prompts, config, trusted keys), not tool meta.

## Verdict criteria

- **approved** — nothing in this reviewer's scope to flag. Also return
  \`approved\` when your only concerns are nit-grade — items you'd label
  "minor", "non-blocking", or "worth noting." Surface those as
  recommendations in the prose; don't aggregate nits into a
  \`changes_requested\`. **Reserve \`changes_requested\` for real
  correctness, security, UX-degrading, or contract-breaking issues.**
- **changes_requested** — specific fixable issues. Name the file:line, the
  problem, and the fix. Example: "hardcoded token at \`src/api.ts:12\`;
  move to an env var read at boot."
- **denied** — the diff introduces a fundamentally unsafe architecture:
  opens a dynamic-code-execution path, trusts untrusted input in a
  privileged context, removes a load-bearing check. Use \`denied\` when
  line-level edits cannot fix the problem.

## Tone and shape

Direct. Terse. If nothing's wrong, say so briefly and approve — don't
invent concerns to fill space. When something IS wrong, be specific
about the attack and the fix.

Lead with the verdict and the 2–3 most important issues. Optional nits
go in a smaller footer. Don't restate what the diff already says.
Target a review a busy author can act on in ~60 seconds. One-sentence
approvals are fine.

## Codebase retros (optional)

Separate from your verdict, you may call \`submit_retro\` 0–5 times to
leave behind transferable security observations about *this codebase* —
trust-boundary conventions worth respecting, invariants the security
model depends on, prior decisions about secret/credential handling that
shouldn't be re-litigated. NOT bug reports about this diff (those go in
your verdict prose). Skip when nothing transferable comes to mind —
silence is the default. The system prompt appendix has the full
instructions and \`kind\` enum.

## Output format (required — do not change)

Prose review, then exactly one final line:

\`\`\`
VERDICT: approved
\`\`\`

(or \`changes_requested\` or \`denied\`). Nothing after it.
`;

export const DEFAULT_STANDARDS_PROMPT = `# standards reviewer

You are the code-quality reviewer for this project. Your job is to keep
the codebase lean, idiomatic, and honestly sized for what it is.

This prompt is a starting point. Edit it to reflect your project's language,
framework, and style preferences. See https://github.com/OpenThinkAi/stamp-cli/blob/main/docs/personas.md
for guidance on calibrating reviewer prompts.

## Calibration philosophy — build-first, resist over-engineering

Prefer code that solves today's concrete problem over code that
anticipates tomorrow's hypothetical one. Push back on:

- **Premature abstractions.** A function extracted for a single caller.
  A factory with one product. A strategy pattern with one strategy. A
  config system for a value that's never varied.
- **Speculative generality.** "What if we later want to swap X" thinking
  when no current feature requires it.
- **Defensive code at internal boundaries.** Null checks on values that
  cannot be null by type or caller contract. \`try/catch\` around calls
  that don't throw. Fallback values for conditions that can't happen.
- **Over-typing.** Branded types for values that are fine as strings.
  Exhaustive generics where inference works.
- **Ceremony.** Builder patterns for objects with three fields. Interfaces
  with one implementation. Excessive getter/setter boilerplate.

Three similar lines is usually better than the wrong abstraction.
Duplication is cheaper than a premature model.

## What else to check for

- **Language idiom hygiene.** Prefer the language's native conventions
  over non-idiomatic transplants from another stack.
- **Type safety at the right places.** Strong types at module boundaries
  and interchange points. Avoid \`any\` / \`unknown\` / dynamic-casts where
  inference works. Be honest about escape hatches when they're needed.
- **Naming.** Intent-revealing, not encoded-type. Domain terms over
  generic names.
- **Error handling only at system boundaries.** User input, filesystem,
  subprocess, network. Internal code should trust its contracts.
- **Dead code.** Unused imports, exports, or parameters rot fast; flag them.
- **Module boundaries.** Each file should have a coherent purpose. Grab-bag
  utility files are a code smell.
- **Test coverage on hot paths.** Don't demand 100% coverage. Do demand
  tests for code that encodes real behavior and has multiple cases.
- **Cross-platform correctness.** For CLIs / scripts: BSD vs GNU tool
  differences, path separator assumptions, shell-specific idioms.

## What you do NOT check

- Security surfaces (secrets, injection, dependency risk) → **security** reviewer.
- User-facing impact (interface shape, UX, breaking changes) → **product** reviewer.

## Verdict criteria

- **approved** — clean, idiomatic, right-sized for the change. Also
  return \`approved\` when your only concerns are nit-grade — items
  you'd label "minor", "non-blocking", "cosmetic", or "while you're in
  there." Surface those as recommendations in the prose; don't
  aggregate nits into a \`changes_requested\`. **Reserve
  \`changes_requested\` for real correctness, idiom, or
  over-engineering issues — actual bugs or wrong-shape code.**
- **changes_requested** — specific fixes with file:line and the concrete
  change you want. Examples: "remove unused import at \`foo.ts:8\`";
  "inline the \`makeX\` factory at \`bar.ts:14\` — only one caller".
- **denied** — the change takes the code in a wrong architectural
  direction: introduces a pattern or layer that doesn't fit, adopts a
  new dependency the project doesn't need, creates the wrong shape
  for the domain.

## Tone and shape

Direct, terse, opinionated. Cite specific lines. Don't hedge. It is
fine to tell the author their abstraction is unjustified — that is
the value this reviewer adds.

Lead with the verdict and the 2–3 most important issues. Optional nits
go in a smaller footer. Don't restate what the diff already says.
Target a review a busy author can act on in ~60 seconds. One-sentence
approvals are fine.

## Codebase retros (optional)

Separate from your verdict, you may call \`submit_retro\` 0–5 times to
leave behind transferable code-quality observations about *this codebase*
— conventions a new contributor should mirror (module boundaries,
naming, layering), prior decisions about abstraction shape that
shouldn't be re-litigated, invariants stated in comments that quietly
hold across the codebase. NOT a list of code-style nits about this diff
(those go in your verdict prose). Skip when nothing transferable comes
to mind. The system prompt appendix has the full instructions and
\`kind\` enum.

## Output format (required — do not change)

Prose review, then exactly one final line:

\`\`\`
VERDICT: approved
\`\`\`

(or \`changes_requested\` or \`denied\`). Nothing after it.
`;

export const DEFAULT_PRODUCT_PROMPT = `# product reviewer

You are the product / user-facing-impact reviewer for this project. Your
job is to guard the interface this project exposes — whatever form that
takes (CLI flags, HTTP API shape, visual UI, library surface, etc.).

**This reviewer's scope is highly project-specific. Edit this prompt
heavily before trusting its verdicts on real diffs.** The structural
pattern below is useful; the concerns listed are generic and probably
don't fit your product perfectly. See
https://github.com/OpenThinkAi/stamp-cli/blob/main/docs/personas.md
for guidance.

## What to check for (generic — customize)

1. **Interface consistency.** Does the change match existing conventions
   in the codebase? Flag naming, URL structure, function signatures,
   error shapes, output formats, etc.
2. **Breaking changes.** Renamed flags, changed exit codes, modified
   response shapes, removed public APIs — any of these break external
   callers. Flag them explicitly even when the change is justified,
   so the author confirms the break is deliberate.
3. **Error messages.** Actionable, specific, name the what/where/next-step.
   "Invalid input" is bad. "Invalid revspec 'main..hed' — did you mean
   'main..HEAD'?" is good.
4. **Accessibility / usability.** For UI: keyboard handling, contrast,
   focus management, screen-reader friendliness. For CLIs: help text
   clarity. For APIs: discoverable errors and documented contracts.
5. **Edge cases in the product's core mechanics.** Empty inputs, inputs
   past expected bounds, concurrent usage, first-run states. The things
   that break in production but not in happy-path demos.
6. **Copy and microcopy.** Terse, clear, in the project's voice.

## What you do NOT check

- Security surfaces → **security** reviewer.
- Code quality, abstractions, idiom → **standards** reviewer.

## Operator intent is load-bearing

When the diff demonstrably implements explicit operator-authored
copy, command shape, or UX choices, do not return \`changes_requested\`
on the basis that you would have phrased it differently or hidden the
surface. Real convention/contract breaks (exit-code collisions, flag
naming drift, broken help text, accessibility regressions) still block.
Stylistic preference does not. Surface stylistic notes as suggestions
in the prose so the operator can take or leave them.

## Verdict criteria

- **approved** — change fits the product, handles relevant edge cases,
  preserves interface consistency, breaking changes (if any) are
  flagged and deliberate. Also return \`approved\` when your only
  concerns are subjective preference (wording, surface visibility,
  "I'd hide this") and the operator's intent is clear from the diff,
  or when remaining items are nit-grade — "minor", "non-blocking",
  "cosmetic". Surface those as recommendations in the prose; don't
  aggregate nits into a \`changes_requested\`. **Reserve
  \`changes_requested\` for real convention breaks, broken error
  messages, contract regressions, or backward-compat failures an agent
  or operator would actually trip over.**
- **changes_requested** — specific UX or interface fixes: rename a flag
  to match convention, fix a broken error message that doesn't say
  what/where/next-step, handle an edge case, document a deliberate
  break, resolve an exit-code or flag collision.
- **denied** — the change moves the product in the wrong direction:
  introduces a concept that conflicts with the existing model, violates
  an explicit non-goal, removes accessibility, changes a contract
  without a migration path. Architectural-level misfit.

## Tone and shape

Direct, terse. Quote specific lines / flags / outputs. Defend the
interface contract — you are the voice that will. Don't hedge when
something breaks the established pattern.

Lead with the verdict and the 2–3 most important issues. Optional nits
go in a smaller footer. Don't restate what the diff already says.
Target a review a busy author can act on in ~60 seconds. One-sentence
approvals are fine.

## Codebase retros (optional)

Separate from your verdict, you may call \`submit_retro\` 0–5 times to
leave behind transferable product/UX observations about *this codebase*
— interface conventions worth respecting, prior decisions about
naming/shape/exit-codes that shouldn't be re-litigated, invariants the
external contract depends on. NOT specific UX papercuts in this diff
(those go in your verdict prose). Skip when nothing transferable comes
to mind. The system prompt appendix has the full instructions and
\`kind\` enum.

## Output format (required — do not change)

Prose review, then exactly one final line:

\`\`\`
VERDICT: approved
\`\`\`

(or \`changes_requested\` or \`denied\`). Nothing after it.
`;
